// assets/AllCourses/Exercises/CompleteTheSentencesExercise.jsx
"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardDescription } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { StringField, TextareaField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

export default function CompleteTheSentencesExercise({
  exercise = {},
  onUpdate,           // Present only in edit mode
  errors = {},
  isPreview = false,  // false = admin edit, true = student view
  showResult = false,
  onComplete = () => {}, // Student: report correct/incorrect
}) {
  const isEditMode = typeof onUpdate === "function";

  // Safe questions array
  const questions = Array.isArray(exercise.sentenceCompletionQuestions)
    ? exercise.sentenceCompletionQuestions
    : [];

  // Student state
  const [answers, setAnswers] = useState({});
  const [hasInteracted, setHasInteracted] = useState(false);
  const completedRef = useRef(false);

  // Sync saved attempt (student mode)
  useEffect(() => {
    if (!isPreview || !exercise.attempt) return;
    setAnswers(exercise.attempt.answers || {});
    setHasInteracted(!!exercise.attempt.hasInteracted);
  }, [isPreview, exercise.attempt]);

  // === EDIT MODE FUNCTIONS ===
  const updateQuestions = (next) => {
    if (isEditMode) onUpdate("sentenceCompletionQuestions", next);
  };

  const handleQuestionChange = (qIdx, field, value) => {
    if (!isEditMode) return;

    const next = [...questions];
    const current = next[qIdx] || {
      text: "",
      options: [],
      answer: "",
      requireCapitalization: false,
    };

    if (field === "options") {
      const options = value.split(",").map((o) => o.trim()).filter(Boolean);
      next[qIdx] = {
        ...current,
        options,
        answer: options.includes(current.answer) ? current.answer : "",
      };
    } else {
      next[qIdx] = { ...current, [field]: value };
    }

    updateQuestions(next);
  };

  const handleCapitalizationChange = (qIdx, checked) => {
    if (!isEditMode) return;
    const next = [...questions];
    next[qIdx] = {
      ...next[qIdx],
      requireCapitalization: Boolean(checked),
    };
    updateQuestions(next);
  };

  const handleAddQuestion = () => {
    if (!isEditMode) return;
    updateQuestions([
      ...questions,
      {
        text: "",
        options: [],
        answer: "",
        requireCapitalization: false,
      },
    ]);
  };

  const handleRemoveQuestion = (qIdx) => {
    if (!isEditMode) return;
    updateQuestions(questions.filter((_, i) => i !== qIdx));
  };

  // === STUDENT MODE FUNCTIONS ===
  const handleChange = (qIndex, value) => {
    if (isEditMode || showResult) return;

    if (!hasInteracted) setHasInteracted(true);

    const updated = { ...answers, [qIndex]: value };
    setAnswers(updated);

    const allAnswered = questions.every((_, i) => updated[i] !== undefined);
    if (allAnswered) {
      checkAnswers(updated);
    }
  };

  const checkAnswers = (finalAnswers) => {
    if (completedRef.current || isEditMode) return;
    completedRef.current = true;

    const allCorrect = questions.every((q, idx) => {
      const user = String(finalAnswers[idx] || "").trim();
      const correct = String(q.answer || "").trim();

      if (q.requireCapitalization) {
        return user === correct;
      }
      return user.toLowerCase() === correct.toLowerCase();
    });

    onComplete(exercise._id, allCorrect);
  };

  // === EDIT MODE RENDER ===
  if (isEditMode) {
    return (
      <div className="space-y-6">
        {questions.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No sentences yet. Click "Add Question" to begin.
          </p>
        )}

        {questions.map((q, qIdx) => (
          <Card key={qIdx} className="p-4 space-y-4">
            <TextareaField
              label={`Sentence ${qIdx + 1}`}
              value={q.text || ""}
              onChange={(v) => handleQuestionChange(qIdx, "text", v)}
              placeholder="She ___ to school every day."
              rows={3}
              required
              error={errors?.[`exercise-${exercise.index}-sc-text-${qIdx}`]}
            />

            <StringField
              label="Options (comma separated)"
              value={(q.options || []).join(", ")}
              onChange={(v) => handleQuestionChange(qIdx, "options", v)}
              placeholder="goes, go, going"
              required
              error={errors?.[`exercise-${exercise.index}-sc-options-${qIdx}`]}
            />

            <StringField
              label="Correct Answer"
              value={q.answer || ""}
              onChange={(v) => handleQuestionChange(qIdx, "answer", v)}
              placeholder="goes"
              required
              error={errors?.[`exercise-${exercise.index}-sc-answer-${qIdx}`]}
            />

            <CapitalizationRule
              checked={!!q.requireCapitalization}
              onChange={(c) => handleCapitalizationChange(qIdx, c)}
            />

            <Button
              variant="destructive"
              size="sm"
              onClick={() => handleRemoveQuestion(qIdx)}
            >
              Remove Question
            </Button>
          </Card>
        ))}

        <Button variant="outline" onClick={handleAddQuestion}>
          Add Question
        </Button>
      </div>
    );
  }

  // === STUDENT PREVIEW MODE ===
  if (questions.length === 0) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardDescription>
            This sentence completion exercise is not configured correctly.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="max-w-3xl mx-auto">
      <CardHeader>
        <CardDescription>
          Complete each sentence by choosing the correct option.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-8">
        {questions.map((q, qIndex) => (
          <div key={qIndex} className="space-y-4">
            {/* Sentence */}
            <div className="bg-muted p-5 rounded-lg text-base font-medium leading-relaxed">
              {q.text || "No sentence provided."}
            </div>

            {/* Options */}
            <RadioGroup
              value={answers[qIndex] || ""}
              onValueChange={(v) => handleChange(qIndex, v)}
              disabled={showResult}
            >
              <div className="space-y-3">
                {q.options.map((option, oIdx) => {
                  const selected = answers[qIndex] === option;
                  const correct = option.trim() === q.answer.trim();

                  return (
                    <div
                      key={oIdx}
                      className="flex items-center space-x-3 p-3 rounded-lg border bg-card"
                    >
                      <RadioGroupItem
                        value={option}
                        id={`cts-${qIndex}-${oIdx}`}
                        className={cn(
                          showResult && correct && "border-green-500",
                          showResult && selected && !correct && "border-red-500"
                        )}
                      />
                      <Label
                        htmlFor={`cts-${qIndex}-${oIdx}`}
                        className={cn(
                          "flex-1 cursor-pointer text-base",
                          showResult && correct && "text-green-700 font-semibold",
                          showResult && selected && !correct && "text-red-700 font-semibold"
                        )}
                      >
                        {option}
                        {showResult && selected && (
                          <span className="ml-3 text-sm">
                            {correct ? "Correct" : `Wrong → ${q.answer}`}
                          </span>
                        )}
                      </Label>
                    </div>
                  );
                })}
              </div>
            </RadioGroup>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}